import { useEffect, useRef } from 'react';
import { Icon } from '@kiips/ui';
import { Linking, Pressable } from 'react-native';
import { createURL } from 'expo-linking';
import { Text, XStack, YStack } from 'tamagui';
import { useAuth } from '../../contexts/AuthContext';
import { trpc } from '../../lib/trpc';
import { track } from '../../lib/analytics';
import { colors } from '../../theme/colors';

/**
 * Dunning banner shown when the user's last subscription payment failed.
 * The `invoice.payment_failed` Stripe webhook sets
 * `user.paymentStatus='past_due'` (see `apps/api/src/webhook/stripe.ts`);
 * `auth.me` surfaces it and `PaymentStatusSync` loads it into AuthContext.
 *
 * The CTA opens the Stripe billing portal so the user can update their
 * payment method — the same flow the profile "Manage subscription" link
 * uses. Intentionally NON-dismissible while past_due: this is account
 * health, not an upsell, and the only fix is updating the card. The
 * banner disappears on its own once a retry succeeds (the webhook clears
 * `paymentStatus`, and the next `auth.me` refresh drops it from context).
 *
 * Renders `null` for every other state, so call sites can mount it
 * unconditionally at the top of a screen.
 */
export function PastDueBanner() {
  const { user } = useAuth();
  const isPastDue = user?.paymentStatus === 'past_due';

  const portal = trpc.billing.createBillingPortalSession.useMutation({
    onSuccess: ({ url }) => {
      if (url) void Linking.openURL(url);
    },
  });

  // Fire the impression once per mount while past_due. A background
  // auth.me refresh that re-derives the same status shouldn't re-count
  // the banner against the dunning-recovery funnel.
  const impressionFired = useRef(false);
  useEffect(() => {
    if (!isPastDue || impressionFired.current) return;
    impressionFired.current = true;
    track('billing.dunning_banner_shown', { tier: user.subscriptionTier });
  }, [isPastDue, user?.subscriptionTier]);

  if (!isPastDue) return null;

  const onUpdatePayment = () => {
    track('billing.dunning_cta_clicked', { tier: user.subscriptionTier });
    portal.mutate({ returnUrl: createURL('/profile') });
  };

  return (
    <XStack
      marginHorizontal={12}
      marginBottom={8}
      $md={{ marginHorizontal: 0 }}
      backgroundColor={colors.accentCrimson + '14'}
      borderRadius={14}
      paddingHorizontal={14}
      paddingVertical={12}
      alignItems="center"
      gap={12}
    >
      <Icon name="error-outline" size={20} color={colors.accentCrimson} />
      <YStack flex={1} minWidth={0}>
        <Text fontSize={13} fontWeight="700" color="$textOnSurface">
          Payment past due
        </Text>
        <Text fontSize={12} color="$textSubdued">
          Your last payment failed. Update your payment method to keep Pro.
        </Text>
      </YStack>
      <Pressable
        onPress={onUpdatePayment}
        disabled={portal.isPending}
        accessibilityRole="button"
        accessibilityLabel="Update payment method"
        hitSlop={8}
        style={({ pressed }) => ({ opacity: pressed || portal.isPending ? 0.6 : 1 })}
      >
        <Text fontSize={12} fontWeight="700" color={colors.accentCrimson}>
          {portal.isPending ? 'Opening…' : 'Update'}
        </Text>
      </Pressable>
    </XStack>
  );
}
